import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Check, X, Plus, Trash2, Clock, Loader2, UserCheck, UserX, Send, UserPlus } from 'lucide-react'
import { BackButton } from '@tendbot/ui'
import { getInvites, addInvite, updateInvite, removeInvite, sendInviteEmail, createUser, type Invite } from './api'
import { SkeletonListItem } from '@tendbot/ui'
import { formatDisplayTime } from '@tendbot/ui'

interface Props {
  onBack: () => void
}

export default function AdminScreen({ onBack }: Props) {
  const { t } = useTranslation('admin')
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newNotes, setNewNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Create-user form state
  const [showCreate, setShowCreate] = useState(false)
  const [cuEmail, setCuEmail] = useState('')
  const [cuPassword, setCuPassword] = useState('')
  const [cuFirstName, setCuFirstName] = useState('')
  const [cuLastName, setCuLastName] = useState('')
  const [cuAssistantName, setCuAssistantName] = useState('')
  const [cuTier, setCuTier] = useState<'trial' | 'managed' | 'pro'>('trial')
  const [cuTrialDays, setCuTrialDays] = useState('14')
  const [cuLanguage, setCuLanguage] = useState<'en' | 'sv'>('en')
  const [cuShowWizard, setCuShowWizard] = useState(false)
  const [cuIsAdmin, setCuIsAdmin] = useState(false)
  const [cuSubmitting, setCuSubmitting] = useState(false)
  const [cuError, setCuError] = useState<string | null>(null)
  const [cuSuccess, setCuSuccess] = useState<string | null>(null)

  // Invite pending deletion (confirm dialog)
  const [confirmRemove, setConfirmRemove] = useState<Invite | null>(null)

  const loadInvites = useCallback(async () => {
    try {
      const data = await getInvites()
      setInvites(data)
    } catch (err) {
      console.error('Failed to load invites:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadInvites() }, [loadInvites])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      const invite = await addInvite(newEmail.trim(), newNotes.trim() || undefined)
      setInvites(prev => [invite, ...prev.filter(i => i.id !== invite.id)])
      setNewEmail('')
      setNewNotes('')
      setShowAdd(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToAdd'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async (id: string) => {
    setActionLoading(id)
    try {
      const updated = await updateInvite(id, 'approved')
      setInvites(prev => prev.map(i => i.id === id ? updated : i))
    } catch (err) {
      console.error('Failed to approve:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (id: string) => {
    setActionLoading(id)
    try {
      const updated = await updateInvite(id, 'rejected')
      setInvites(prev => prev.map(i => i.id === id ? updated : i))
    } catch (err) {
      console.error('Failed to reject:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleSendInvite = async (id: string) => {
    setActionLoading(id)
    try {
      await sendInviteEmail(id)
      // Mark as sent in local state by updating notes
      setInvites(prev => prev.map(i =>
        i.id === id ? { ...i, notes: i.notes ? `${i.notes} (invite sent)` : '(invite sent)' } : i,
      ))
    } catch (err) {
      // Show the error message (e.g. "already registered")
      const msg = err instanceof Error ? err.message : 'Failed to send'
      setError(msg)
    } finally {
      setActionLoading(null)
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!cuEmail.trim() || !cuPassword.trim()) return
    setCuSubmitting(true)
    setCuError(null)
    setCuSuccess(null)
    try {
      const trialDaysNum = parseInt(cuTrialDays, 10)
      const created = await createUser({
        email: cuEmail.trim(),
        password: cuPassword,
        firstName: cuFirstName.trim() || undefined,
        lastName: cuLastName.trim() || undefined,
        assistantName: cuAssistantName.trim() || undefined,
        tier: cuTier,
        trialDays: Number.isFinite(trialDaysNum) ? trialDaysNum : 14,
        language: cuLanguage,
        showOnboardingWizard: cuShowWizard,
        isAdmin: cuIsAdmin,
      })
      setCuSuccess(t('userCreated', { email: created.email }))
      setCuEmail('')
      setCuPassword('')
      setCuFirstName('')
      setCuLastName('')
      setCuAssistantName('')
      setCuTier('trial')
      setCuTrialDays('14')
      setCuLanguage('en')
      setCuShowWizard(false)
      setCuIsAdmin(false)
      // Refresh invite list so the new user appears under Approved
      await loadInvites()
    } catch (err) {
      setCuError(err instanceof Error ? err.message : t('failedToCreateUser'))
    } finally {
      setCuSubmitting(false)
    }
  }

  const requestRemove = (id: string) => {
    const target = invites.find(i => i.id === id)
    if (target) setConfirmRemove(target)
  }

  const handleRemoveConfirmed = async () => {
    if (!confirmRemove) return
    const id = confirmRemove.id
    setActionLoading(id)
    setConfirmRemove(null)
    try {
      await removeInvite(id)
      setInvites(prev => prev.filter(i => i.id !== id))
    } catch (err) {
      console.error('Failed to remove:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const pending = invites.filter(i => i.status === 'pending')
  const approved = invites.filter(i => i.status === 'approved')
  const rejected = invites.filter(i => i.status === 'rejected')

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-5 sm:px-8 lg:px-12 py-4 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BackButton onClick={onBack} />
          <h1 className="font-display text-2xl text-ivory font-semibold">{t('title')}</h1>
        </div>

        {/* Add Invite / Create User Buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => { setShowAdd(!showAdd); setShowCreate(false) }}
            className="py-3 rounded-xl border border-dashed border-gold/20 text-gold/60 hover:text-gold hover:border-gold/40 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            {t('addInvite')}
          </button>
          <button
            onClick={() => { setShowCreate(!showCreate); setShowAdd(false) }}
            className="py-3 rounded-xl border border-dashed border-gold/20 text-gold/60 hover:text-gold hover:border-gold/40 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus size={16} />
            {t('createUser')}
          </button>
        </div>

        {/* Add Form */}
        <AnimatePresence>
          {showAdd && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-4"
              onSubmit={handleAdd}
            >
              <div className="glass-card rounded-xl p-4 space-y-3">
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  required
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                  autoFocus
                />
                <input
                  type="text"
                  value={newNotes}
                  onChange={e => setNewNotes(e.target.value)}
                  placeholder={t('notesPlaceholder')}
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                />
                {error && (
                  <p className="text-rose text-xs">{error}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gold hover:bg-gold-light text-midnight font-semibold py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {t('invite')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAdd(false); setError(null) }}
                    className="px-4 py-2.5 rounded-lg border border-slate-mid/30 text-pearl/60 text-sm hover:text-pearl hover:border-slate-mid/50 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Create User Form */}
        <AnimatePresence>
          {showCreate && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-4"
              onSubmit={handleCreateUser}
            >
              <div className="glass-card rounded-xl p-4 space-y-3">
                <p className="text-pearl/70 text-xs leading-relaxed">{t('createUserHint')}</p>
                <input
                  type="email"
                  value={cuEmail}
                  onChange={e => setCuEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  required
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                  autoFocus
                />
                <input
                  type="text"
                  value={cuPassword}
                  onChange={e => setCuPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder')}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={cuFirstName}
                    onChange={e => setCuFirstName(e.target.value)}
                    placeholder={t('firstNamePlaceholder')}
                    className="bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                  />
                  <input
                    type="text"
                    value={cuLastName}
                    onChange={e => setCuLastName(e.target.value)}
                    placeholder={t('lastNamePlaceholder')}
                    className="bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                  />
                </div>
                <input
                  type="text"
                  value={cuAssistantName}
                  onChange={e => setCuAssistantName(e.target.value)}
                  placeholder={t('assistantNamePlaceholder')}
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                />
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={cuTier}
                    onChange={e => setCuTier(e.target.value as 'trial' | 'managed' | 'pro')}
                    className="bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm focus:outline-none focus:border-gold/40 transition-colors"
                  >
                    <option value="trial">{t('tierTrial')}</option>
                    <option value="managed">{t('tierManaged')}</option>
                    <option value="pro">{t('tierPro')}</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={cuTrialDays}
                    onChange={e => setCuTrialDays(e.target.value)}
                    disabled={cuTier !== 'trial'}
                    placeholder={t('trialDaysPlaceholder')}
                    className="bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors disabled:opacity-50"
                  />
                  <select
                    value={cuLanguage}
                    onChange={e => setCuLanguage(e.target.value as 'en' | 'sv')}
                    disabled={cuShowWizard}
                    className="bg-slate-dark/80 border border-gold/10 rounded-lg px-3 py-2.5 text-ivory text-sm focus:outline-none focus:border-gold/40 transition-colors disabled:opacity-50"
                  >
                    <option value="en">English</option>
                    <option value="sv">Svenska</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <label className="flex items-center gap-2 text-pearl/80 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cuShowWizard}
                      onChange={e => setCuShowWizard(e.target.checked)}
                      className="accent-gold"
                    />
                    {t('showOnboardingWizard')}
                  </label>
                  <label className="flex items-center gap-2 text-pearl/80 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cuIsAdmin}
                      onChange={e => setCuIsAdmin(e.target.checked)}
                      className="accent-gold"
                    />
                    {t('makeAdmin')}
                  </label>
                </div>
                {cuError && (
                  <p className="text-rose text-xs">{cuError}</p>
                )}
                {cuSuccess && (
                  <p className="text-emerald text-xs">{cuSuccess}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={cuSubmitting}
                    className="flex-1 bg-gold hover:bg-gold-light text-midnight font-semibold py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {cuSubmitting && <Loader2 size={14} className="animate-spin" />}
                    {t('createUser')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCreate(false); setCuError(null); setCuSuccess(null) }}
                    className="px-4 py-2.5 rounded-lg border border-slate-mid/30 text-pearl/60 text-sm hover:text-pearl hover:border-slate-mid/50 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {loading ? (
          <div className="space-y-3">
            <SkeletonListItem />
            <SkeletonListItem />
            <SkeletonListItem />
          </div>
        ) : (
          <>
            {/* Pending Requests */}
            {pending.length > 0 && (
              <Section title={t('sectionPending')} count={pending.length}>
                {pending.map(invite => (
                  <InviteRow
                    key={invite.id}
                    invite={invite}
                    actionLoading={actionLoading}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemove={requestRemove}
                    onSendInvite={handleSendInvite}
                  />
                ))}
              </Section>
            )}

            {/* Approved */}
            {approved.length > 0 && (
              <Section title={t('sectionApproved')} count={approved.length}>
                {approved.map(invite => (
                  <InviteRow
                    key={invite.id}
                    invite={invite}
                    actionLoading={actionLoading}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemove={requestRemove}
                    onSendInvite={handleSendInvite}
                  />
                ))}
              </Section>
            )}

            {/* Rejected */}
            {rejected.length > 0 && (
              <Section title={t('sectionRejected')} count={rejected.length}>
                {rejected.map(invite => (
                  <InviteRow
                    key={invite.id}
                    invite={invite}
                    actionLoading={actionLoading}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemove={requestRemove}
                    onSendInvite={handleSendInvite}
                  />
                ))}
              </Section>
            )}

            {invites.length === 0 && (
              <p className="text-pearl/60 text-sm text-center py-8">{t('empty')}</p>
            )}
          </>
        )}
      </div>

      <ConfirmRemoveModal
        invite={confirmRemove}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={handleRemoveConfirmed}
      />
    </div>
  )
}

function ConfirmRemoveModal({ invite, onCancel, onConfirm }: {
  invite: Invite | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('admin')

  useEffect(() => {
    if (!invite) return
    document.body.style.overflow = 'hidden'
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [invite, onCancel])

  if (!invite) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-remove-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-rose/20 bg-charcoal shadow-[0_24px_60px_rgba(0,0,0,0.40)] p-6 sm:p-8 animate-in slide-in-from-bottom-4 duration-300"
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl bg-gradient-to-r from-transparent via-rose/60 to-transparent" />

        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-rose/10 border border-rose/20 flex items-center justify-center">
            <Trash2 size={26} className="text-rose" />
          </div>
        </div>

        <div className="text-center mb-6">
          <h3 id="confirm-remove-title" className="font-display text-2xl text-ivory mb-2">
            {t('confirmRemoveTitle')}
          </h3>
          <p className="text-sm text-pearl">
            {t('confirmRemoveDescription', { email: invite.email })}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl bg-slate-mid/50 border border-slate-mid text-pearl font-medium text-sm hover:bg-slate-mid/80 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            className="flex-1 py-3 rounded-xl bg-rose/80 text-white font-semibold text-sm hover:bg-rose transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            {t('remove')}
          </button>
        </div>
      </div>
    </div>
  )
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-pearl/50 text-xs font-semibold uppercase tracking-wider">{title}</h3>
        <span className="text-pearl/55 text-xs">({count})</span>
      </div>
      <div className="glass-card rounded-xl overflow-hidden divide-y divide-slate-mid/30">
        {children}
      </div>
    </div>
  )
}

function InviteRow({ invite, actionLoading, onApprove, onReject, onRemove, onSendInvite }: {
  invite: Invite
  actionLoading: string | null
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onRemove: (id: string) => void
  onSendInvite: (id: string) => void
}) {
  const isLoading = actionLoading === invite.id

  const statusIcon = invite.status === 'pending'
    ? <Clock size={14} className="text-amber" />
    : invite.status === 'approved'
      ? <UserCheck size={14} className="text-emerald" />
      : <UserX size={14} className="text-rose/60" />

  return (
    <div className="flex items-center gap-3 p-3.5">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        invite.status === 'pending' ? 'bg-amber/10' :
        invite.status === 'approved' ? 'bg-emerald/10' : 'bg-rose/10'
      }`}>
        {statusIcon}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-ivory text-sm truncate">{invite.email}</p>
        <div className="flex items-center gap-2">
          <span className="text-pearl/60 text-xs">{formatDisplayTime(invite.requested_at)}</span>
          {invite.notes && (
            <span className="text-pearl/55 text-xs truncate">&middot; {invite.notes}</span>
          )}
        </div>
      </div>

      {isLoading ? (
        <Loader2 size={16} className="text-pearl/55 animate-spin" />
      ) : invite.status === 'pending' ? (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onApprove(invite.id)}
            className="w-8 h-8 rounded-lg bg-emerald/10 hover:bg-emerald/20 flex items-center justify-center transition-colors"
            title="Approve"
          >
            <Check size={14} className="text-emerald" />
          </button>
          <button
            onClick={() => onReject(invite.id)}
            className="w-8 h-8 rounded-lg bg-rose/10 hover:bg-rose/20 flex items-center justify-center transition-colors"
            title="Reject"
          >
            <X size={14} className="text-rose" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          {invite.status === 'approved' && (
            <button
              onClick={() => onSendInvite(invite.id)}
              className="w-8 h-8 rounded-full bg-gold/10 hover:bg-gold/20 flex items-center justify-center transition-colors"
              title="Send invite email"
            >
              <Send size={14} className="text-gold" />
            </button>
          )}
          <button
            onClick={() => onRemove(invite.id)}
            className="w-8 h-8 rounded-lg hover:bg-slate-mid/30 flex items-center justify-center transition-colors"
            title="Remove"
          >
            <Trash2 size={14} className="text-pearl/55 hover:text-rose/60" />
          </button>
        </div>
      )}
    </div>
  )
}
